import React, { useState, useEffect } from "react";
import TransactionService from "../../../services/api/transaction.service";
import { Loader } from "lucide-react";
const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    skip: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    startDate: "",
    endDate: "",
    limit: 50,
    skip: 0,
  });

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response = await TransactionService.getTransactionHistory(
          filters
        );
        if (response && response.success) {
          setTransactions(response.data?.data || []);
          setPagination(
            response.data?.pagination || {
              total: 0,
              limit: 50,
              skip: 0,
              currentPage: 1,
              totalPages: 1,
            }
          );
          setLoading(false);
        } else {
          throw new Error((response && response.error) || "Unknown error");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch transactions");
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      skip: 0, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    const newSkip = (newPage - 1) * (pagination?.limit || 50);
    setFilters((prev) => ({
      ...prev,
      skip: newSkip,
    }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      skip: 0, // Reset to first page when limit changes
    }));
  };

  const resetFilters = () => {
    setFilters({
      type: "",
      status: "",
      startDate: "",
      endDate: "",
      limit: 50,
      skip: 0,
    });
  };

  if (loading) {
    return <Loader className="animate-spin text-indigo-500 mb-4" size={36} />;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Transaction History</h2>

      {/* Filter Form */}
      <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              name="type"
              value={filters.type || ""}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">All Types</option>
              <option value="DEPOSIT">Deposit</option>
              <option value="INVESTMENT">Investment</option>
              <option value="WITHDRAWAL">Withdrawal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              name="status"
              value={filters.status || ""}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">All Statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate || ""}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate || ""}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={resetFilters}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2 px-4 border-b text-left">Date</th>
              <th className="py-2 px-4 border-b text-left">Type</th>
              <th className="py-2 px-4 border-b text-left">Description</th>
              <th className="py-2 px-4 border-b text-left">Amount</th>
              <th className="py-2 px-4 border-b text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {(transactions || []).map((transaction) => (
              <tr
                key={transaction?._id || Math.random().toString()}
                className="hover:bg-gray-50"
              >
                <td className="py-2 px-4 border-b">
                  {transaction?.timestamp
                    ? new Date(transaction.timestamp).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="py-2 px-4 border-b">
                  {transaction?.type || "N/A"}
                </td>
                <td className="py-2 px-4 border-b">
                  {transaction?.description || "N/A"}
                </td>
                <td className="py-2 px-4 border-b">
                  {transaction?.amount !== null &&
                  transaction?.amount !== undefined
                    ? `${Number(transaction.amount).toFixed(2)} ${
                        transaction?.wallet?.currency || ""
                      }`
                    : "N/A"}
                </td>
                <td className="py-2 px-4 border-b">
                  <span
                    className={`px-2 py-1 rounded ${
                      transaction?.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : transaction?.status === "PENDING"
                        ? "bg-yellow-100 text-yellow-800"
                        : transaction?.status === "FAILED"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {transaction?.status || "N/A"}
                  </span>
                </td>
              </tr>
            ))}
            {(!transactions || transactions.length === 0) && (
              <tr>
                <td colSpan="5" className="py-4 text-center text-gray-500">
                  No transactions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="mt-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Show per page:</label>
          <select
            value={filters.limit}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                limit: parseInt(e.target.value, 10),
                skip: 0,
              }));
            }}
            className="border border-gray-300 rounded-md p-1"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <nav className="flex items-center justify-center gap-1">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="p-2 rounded-full border border-gray-300 bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous"
          >
            &larr;
          </button>
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter(
              (page) =>
                page === 1 ||
                page === pagination.totalPages ||
                (page >= pagination.currentPage - 1 &&
                  page <= pagination.currentPage + 1)
            )
            .map((page, idx, arr) => {
              // Add ellipsis if needed
              if (idx > 0 && page - arr[idx - 1] > 1) {
                return (
                  <span key={`ellipsis-${page}`} className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 rounded-full border transition
                    ${
                      pagination.currentPage === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600"
                    }
                  `}
                  aria-current={
                    pagination.currentPage === page ? "page" : undefined
                  }
                >
                  {page}
                </button>
              );
            })}
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="p-2 rounded-full border border-gray-300 bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next"
          >
            &rarr;
          </button>
        </nav>
      </div>
    </div>
  );
};

export default TransactionHistory;
